import { FC } from 'react';
import { useInView } from 'react-intersection-observer';
import { useSelector } from 'react-redux';

import { getMarket, getPlatform } from '@/domains/core/settings/utils';
import { Detail } from '@/domains/productDiscovery/ProductPage/b2c/interfaces/Detail.model';
import { PlaylistGraphQL } from '@/domains/productDiscovery/Recommendation/components/PlaylistGraphQL/PlaylistGraphQL';
import { useGetProductCardsQuery } from '@/domains/productDiscovery/Recommendation/services/graphql/generated/getProductCards.query.generated';
import { Market, Platform } from '@/generated/graphql/types';
import {
  selectProductHistoryFetched,
  selectProductHistoryIds,
} from '@/productDiscovery/Recommendation/selectors/productHistory';

import { recentlyViewedTitle } from './playlistProductHistory.translations';

const MIN_NUMBER_OF_PRODUCTS = 3;

type Props = {
  productId?: Detail['productId'];
};

export const PlaylistProductHistory: FC<Props> = ({ productId }) => {
  const productHistoryIds = useSelector(selectProductHistoryIds) || [];
  const productHistoryIdsFetched = useSelector(selectProductHistoryFetched);

  const [ref, isInView] = useInView({ triggerOnce: true });

  const historyIds = productHistoryIds
    .map((history) => history.productId.toString())
    .filter((id) => id !== String(productId));

  const [{ data, error }] = useGetProductCardsQuery({
    pause:
      (productHistoryIds?.length ?? 0) < MIN_NUMBER_OF_PRODUCTS || !isInView,
    variables: {
      offerOptions: { acceptNonSellable: false },
      productIds: historyIds,
      saleChannel: {
        market: getMarket().toUpperCase() as Market,
        platform: getPlatform().toUpperCase() as Platform,
      },
    },
  });

  if (error) return null;
  if (productHistoryIdsFetched && !historyIds.length) return null;
  if ((historyIds.length ?? 0) < MIN_NUMBER_OF_PRODUCTS) return null;
  if (data?.offersByProductIds?.offers.length === 0) return null;

  const unsortedProducts = data?.offersByProductIds?.offers || [];

  const products = unsortedProducts.sort(
    (a, b) => historyIds.indexOf(a.productId) - historyIds.indexOf(b.productId),
  );

  return (
    <div ref={ref}>
      <PlaylistGraphQL
        engineName="history"
        isFetching={!data}
        products={products}
        title={recentlyViewedTitle}
      />
    </div>
  );
};

PlaylistProductHistory.displayName = 'PlaylistProductHistory';
